import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    service: '',
    vehicle: '',
    message: '',
    preferredDate: '',
    preferredTime: '',
    newsletter: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const locationOptions = [
    { value: '', label: 'Standort wählen' },
    { value: 'dinslaken', label: 'Dinslaken - Hauptstraße 123' }
  ];

  const serviceOptions = [
    { value: '', label: 'Service wählen' },
    { value: 'nano', label: 'NANO-Versiegelung (ab 299€)' },
    { value: 'repair', label: 'Lackreparatur (ab 149€)' },
    { value: 'polish', label: 'Premium Politur (ab 199€)' },
    { value: 'cleaning', label: 'Komplettreinigung (ab 89€)' },
    { value: 'exterior', label: 'Außenhandwäsche (ab 49€)' },
    { value: 'interior', label: 'Innenraumaufbereitung (ab 79€)' },
    { value: 'complete', label: 'Komplettaufbereitung (ab 299€)' },
    { value: 'consultation', label: 'Beratungstermin (kostenlos)' }
  ];

  const timeOptions = [
    { value: '', label: 'Uhrzeit wählen' },
    { value: '08:00', label: '08:00 Uhr' },
    { value: '09:00', label: '09:00 Uhr' },
    { value: '10:00', label: '10:00 Uhr' },
    { value: '11:00', label: '11:00 Uhr' },
    { value: '12:00', label: '12:00 Uhr' },
    { value: '13:00', label: '13:00 Uhr' },
    { value: '14:00', label: '14:00 Uhr' },
    { value: '15:00', label: '15:00 Uhr' },
    { value: '16:00', label: '16:00 Uhr' },
    { value: '17:00', label: '17:00 Uhr' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefonnummer ist erforderlich';
    }

    if (!formData?.location) {
      newErrors.location = 'Bitte wählen Sie einen Standort';
    }

    if (!formData?.service) {
      newErrors.service = 'Bitte wählen Sie einen Service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        service: '',
        vehicle: '',
        message: '',
        preferredDate: '',
        preferredTime: '',
        newsletter: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-20 bg-gradient-to-br from-success/10 via-white to-accent/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl shadow-card p-12">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} className="text-success" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Vielen Dank für Ihre Anfrage!
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Wir haben Ihre Terminanfrage erhalten und melden uns innerhalb von 2 Stunden bei Ihnen.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Nächste Schritte:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Terminbestätigung per E-Mail</li>
                  <li>✓ Erinnerung 24h vorher</li>
                  <li>✓ Kostenvoranschlag vor Ort</li>
                </ul>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="font-semibold text-foreground mb-2">Sofort erreichbar:</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>📞 Dinslaken: 02064 777722</div>
                  <div>💬 WhatsApp verfügbar</div>
                </div>
              </div>
            </div>
            <Button
              variant="default"
              className="bg-success hover:bg-success/90"
              onClick={() => setIsSubmitted(false)}
            >
              Weitere Anfrage stellen
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-primary/5 via-white to-accent/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="Calendar" size={16} />
            <span className="text-sm font-medium">Termin vereinbaren</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Jetzt Termin
            <span className="block text-accent">buchen</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Vereinbaren Sie noch heute einen Termin für die professionelle Aufbereitung Ihres Fahrzeugs. 
            Wir melden uns innerhalb von 2 Stunden bei Ihnen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Form */}
          {/* <div className="bg-white rounded-2xl shadow-card p-8">
            <h3 className="text-2xl font-bold text-foreground mb-6">
              Online Terminbuchung
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              {/* <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Vor- und Nachname"
                  type="text"
                  name="name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  placeholder="Max Mustermann"
                  error={errors?.name}
                  required
                />
                <Input
                  label="E-Mail-Adresse"
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  placeholder="max@beispiel.de"
                  error={errors?.email}
                  required
                />
              </div>

              <Input
                label="Telefonnummer"
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleInputChange}
                placeholder="0123 456789"
                error={errors?.phone}
                required
              />

              {/* Service Selection */}
              {/* <div className="grid md:grid-cols-2 gap-4">
                <Select
                  label="Standort"
                  options={locationOptions}
                  value={formData?.location}
                  onChange={(value) => handleSelectChange('location', value)}
                  error={errors?.location}
                  required
                />
                <Select
                  label="Gewünschter Service"
                  options={serviceOptions}
                  value={formData?.service}
                  onChange={(value) => handleSelectChange('service', value)}
                  error={errors?.service}
                  required
                />
              </div>

              <Input
                label="Fahrzeug (optional)"
                type="text"
                name="vehicle"
                value={formData?.vehicle}
                onChange={handleInputChange}
                placeholder="z.B. BMW 3er Touring, Baujahr 2020"
              />

              {/* Date and Time */}
              {/* <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Wunschtermin (optional)"
                  type="date"
                  name="preferredDate"
                  value={formData?.preferredDate}
                  onChange={handleInputChange}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                <Select
                  label="Wunschzeit (optional)"
                  options={timeOptions}
                  value={formData?.preferredTime}
                  onChange={(value) => handleSelectChange('preferredTime', value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nachricht (optional)
                </label>
                <textarea
                  name="message"
                  value={formData?.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                  placeholder="Besondere Wünsche oder Anmerkungen..."
                />
              </div>

              {/* Newsletter Checkbox */}
              {/* <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="newsletter"
                  name="newsletter"
                  checked={formData?.newsletter}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-accent border-border rounded focus:ring-accent"
                />
                <label htmlFor="newsletter" className="text-sm text-muted-foreground">
                  Ich möchte den Newsletter mit Angeboten und Pflegetipps erhalten (jederzeit abbestellbar)
                </label>
              </div>

              {/* Submit Button */}
              {/* <Button
                type="submit"
                variant="default"
                size="lg"
                className="w-full bg-accent hover:bg-accent/90"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Termin anfragen'}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu. 
                Ihre Daten werden nur zur Terminvereinbarung verwendet.
              </p>
            </form>
          </div> */}

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Quick Contact */}
            <div className="bg-white rounded-2xl shadow-card p-6 md:p-8">
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6">
                Sofort erreichbar
              </h3>
              
              <div className="space-y-6">
                {/* Dinslaken */}
                <div className="p-4 bg-primary/5 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="MapPin" size={16} className="text-primary" />
                    <span>Dinslaken (Hauptsitz)</span>
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={14} className="text-primary" />
                      <a href="tel:02064 777722" className="text-primary hover:text-accent">
                        02064 777722
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="MessageCircle" size={14} className="text-success" />
                      <a 
                        href="https://wa.me/491636923382?text=Hallo,%20ich%20möchte%20einen%20Termin%20vereinbaren."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-success hover:text-success/80"
                      >
                        WhatsApp
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={14} className="text-muted-foreground" />
                      <span className="text-muted-foreground">Mo-Fr: 08:00-17:30, Sa: 08:00-16:00</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Beschwerde WhatsApp Button */}
            <div className="bg-white rounded-2xl shadow-card p-6 md:p-8">
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6">
                Beschwerde einreichen
              </h3>
              
              <div className="text-center">
                <p className="text-muted-foreground mb-6">
                  Haben Sie ein Problem mit unserem Service? Wir möchten es so schnell wie möglich lösen.
                </p>
                
                <a
                  href="https://wa.me/491636923382?text=Hallo,%20ich%20möchte%20eine%20Beschwerde%20einreichen."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-3 bg-success text-white px-6 py-3 rounded-full font-semibold hover:bg-success/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                >
                  <Icon name="MessageCircle" size={20} />
                  <span>Beschwerde schicken auf WhatsApp</span>
                </a>
                
                <p className="text-xs text-muted-foreground mt-4">
                  Wir antworten innerhalb von 2 Stunden
                </p>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-2xl p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-6">
                Warum Ragna Detailing?
              </h3>
              <div className="space-y-4">
                {[
                  { icon: 'Award', text: 'Über 15 Jahre Erfahrung' },
                  { icon: 'Users', text: '5000+ zufriedene Kunden' },
                  { icon: 'Shield', text: 'Garantie auf alle Services' },
                  { icon: 'Clock', text: 'Pünktliche Terminabwicklung' },
                  { icon: 'Car', text: 'Modernste Ausrüstung' },
                  { icon: 'Heart', text: 'Leidenschaft für Perfektion' }
                ]?.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name={benefit?.icon} size={16} className="text-accent" />
                    <span className="text-muted-foreground">{benefit?.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Emergency Contact */}
            {/* <div className="bg-white rounded-2xl shadow-card p-8 text-center">
              <h3 className="text-xl font-bold text-foreground mb-4">
                Dringender Termin?
              </h3>
              <p className="text-muted-foreground mb-6">
                Rufen Sie uns direkt an – wir finden eine Lösung!
              </p>
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="w-full max-w-xs"
                  onClick={() => window.open('tel:+4920641234567', '_self')}
                  iconName="Phone"
                  iconPosition="left"
                >
                  Dinslaken anrufen
                </Button>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;